import { AxiosResponse } from "axios";
import {
    call,
    cancel,
    cancelled,
    delay,
    fork,
    put,
    take,
    takeLatest,
} from "redux-saga/effects";
import { Task } from "redux-saga";
import { getActiveAppStoreIssues } from "../../services/app-store-issues/app-store-issues.service";
import {
    clearActiveIssues,
    getActiveIssues,
    getActiveIssuesFailure,
    getActiveIssuesSuccess,
    startActiveIssuesPolling,
    stopActiveIssuesPolling,
} from "../reducer/app-store-issues";
import { IActiveAppStoreIssueResource } from "../types/app-store-issues";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

function isDocumentVisible(): boolean {
    if (typeof document === "undefined") return true;
    return document.visibilityState !== "hidden";
}

function* fetchActiveIssuesSaga() {
    try {
        const response: AxiosResponse = yield call(getActiveAppStoreIssues);
        const list: IActiveAppStoreIssueResource[] = Array.isArray(
            response?.data
        )
            ? response.data
            : [];
        yield put(getActiveIssuesSuccess(list));
    } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
            yield put(clearActiveIssues());
            return;
        }
        yield put(getActiveIssuesFailure());
    }
}

function* pollActiveIssuesSaga() {
    try {
        while (true) {
            if (isDocumentVisible()) {
                yield call(fetchActiveIssuesSaga);
            }
            yield delay(POLL_INTERVAL_MS);
        }
    } finally {
        const wasCancelled = (yield cancelled()) as boolean;
        if (wasCancelled) {
            // polling stopped
        }
    }
}

function* watchActiveIssuesPolling() {
    while (true) {
        yield take(startActiveIssuesPolling.type);
        const pollTask = (yield fork(pollActiveIssuesSaga)) as Task;
        yield take(stopActiveIssuesPolling.type);
        yield cancel(pollTask);
        // Don't clear issues just because polling stopped — a stop fires on
        // every auth reload / effect re-run while the user stays eligible, and
        // clearing here makes the banner flash empty until the next fetch.
        // The banner is gated by eligibility in the view, and genuine auth
        // loss is cleared by the 401/403 branch in fetchActiveIssuesSaga.
    }
}

export function* rootSaga() {
    yield takeLatest(getActiveIssues.type, fetchActiveIssuesSaga);
    yield fork(watchActiveIssuesPolling);
}
